import styled from "@emotion/styled";
import React from "react";
import { Page } from "../../activities/AppPage";
import { STRINGS } from "../../strings";
import { translate } from "../../translate";
import { assertUndefinedable } from "../../utils/assertUndefinedable";
import { INFO_CIRCLE_ICON } from "../../utils/svgs_new";
import { AppIcon } from "../Generic/AppIcon";
import AppSlideFadeIn from "../Generic/AppSlideFadeIn";
import CareMomentRankDemotionModal from "../Generic/CareMoments/components/CareMomentRankDemotionModal";
import CareMomentRankPromotionModal from "../Generic/CareMoments/components/CareMomentRankPromotionModal";
import CareMomentStreakBreakModal from "../Generic/CareMoments/components/CareMomentStreakBreakModal";
import ScaffoldTitle from "../Scaffold/components/ScaffoldTitle";
import Scaffold from "../Scaffold/Scaffold";
import { careMomentsIcons } from "./CareMoments.icons";
import { CareMomentRank, CareMomentRankChange } from "./CareMoments.types";
import { getNextRank, getRankChangeType } from "./CareMoments.utils";
import CareMomentRankIcon from "./components/CareMomentRankIcon";
import RankRoad from "./components/RankRoad";

interface Props {
  ranks: CareMomentRank[];
  rankChange: CareMomentRankChange;
  caregiverAvatarUrl: string | null;
  onClickGoBack: () => void;
  onRequestNavigate: (page: Page) => void;
}

export default function CareMomentsScreen(props: Props) {
  const currentRank = props.rankChange.to.rank;
  const nextRank = getNextRank({
    ranks: props.ranks,
    currentRankId: currentRank.id,
  });
  const scaffoldRef = React.useRef<HTMLDivElement>(null);
  const [isPromotionModalOpen, setIsPromotionModalOpen] = React.useState(false);
  const [isDemotionModalOpen, setIsDemotionModalOpen] = React.useState(false);
  const [isStreakBreakModalOpen, setIsStreakBreakModalOpen] = React.useState(false);
  const handleFinishAnimation = () => {
    const rankChangeType = getRankChangeType({
      rankChange: props.rankChange,
      ranks: props.ranks,
    });

    switch (rankChangeType) {
      case "none":
        return;

      case "promotion":
        setIsPromotionModalOpen(true);
        return;

      case "demotion":
        setIsDemotionModalOpen(true);
        return;

      case "streak-break":
        setIsStreakBreakModalOpen(true);
    }
  };
  const rankIcon =
    assertUndefinedable(careMomentsIcons[currentRank.icon as keyof typeof careMomentsIcons]) ??
    careMomentsIcons["bedside-buddy"];

  return (
    <>
      <Scaffold
        title={<ScaffoldTitle label={translate(STRINGS.CARE_MOMENTS)} />}
        scrollRef={scaffoldRef}
        endActions={
          <AppIcon
            icon={INFO_CIRCLE_ICON}
            size={16}
            onClick={() => props.onRequestNavigate({ type: "CareMomentsExplained" })}
          />
        }
        extraHeader={
          <RankBanner>
            <RankIconWrapper>
              <AppSlideFadeIn delay={100}>
                <CareMomentRankIcon icon={currentRank.icon} size={68} />
              </AppSlideFadeIn>
            </RankIconWrapper>
            <AppSlideFadeIn delay={200}>
              <RankBannerText>
                <h4>
                  {currentRank.title}
                  <small>
                    <span style={{ margin: "0 6px" }}>•</span>
                    <b>${currentRank.wage.toFixed(2)}</b>
                  </small>
                </h4>
                {nextRank && (
                  <span>
                    {translate(STRINGS.NUM_SHIFTS_TO_WAGE)
                      .replace(
                        "{numShifts}",
                        (
                          nextRank.requiredPoints -
                          currentRank.requiredPoints -
                          props.rankChange.to.points
                        ).toString()
                      )
                      .replace("{wage}", "")}
                    <b>${nextRank.wage.toFixed(2)}</b>
                  </span>
                )}
              </RankBannerText>
            </AppSlideFadeIn>
          </RankBanner>
        }
        onClickGoBack={props.onClickGoBack}
      >
        <RankRoad
          scaffoldRef={scaffoldRef}
          ranks={props.ranks}
          rankChange={props.rankChange}
          avatarUrl={props.caregiverAvatarUrl}
          onFinishAnimation={handleFinishAnimation}
        />
      </Scaffold>

      <CareMomentRankPromotionModal
        icon={rankIcon}
        name={currentRank.title}
        wage={currentRank.wage}
        isOpen={isPromotionModalOpen}
        onRequestClose={() => setIsPromotionModalOpen(false)}
      />

      <CareMomentRankDemotionModal
        icon={rankIcon}
        name={currentRank.title}
        wage={currentRank.wage}
        isOpen={isDemotionModalOpen}
        onRequestClose={() => setIsDemotionModalOpen(false)}
      />

      <CareMomentStreakBreakModal
        isOpen={isStreakBreakModalOpen}
        onRequestClose={() => setIsStreakBreakModalOpen(false)}
      />
    </>
  );
}

const RankBanner = styled.div`
  position: relative;
  background: radial-gradient(43.59% 146.96% at 50% 50%, #a6c0f4 0%, #5684e6 100%);
  color: #fff;
  display: flex;
  gap: 4px;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  height: 110px;
  font-weight: 300;
  margin: 28px -24px -24px -24px;

  h4 {
    font-size: 18px;
    font-weight: 500;
    margin: 0;
  }

  b {
    font-weight: 500;
  }
`;

const RankIconWrapper = styled.div`
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: -34px;
`;

const RankBannerText = styled.div`
  padding-bottom: 24px;
  text-align: center;
`;
